// [목적] AI 자연어 보정 입력 바 — 텍스트 입력 + 예시 칩 + 전송, FolioEditPage/FolioMobile 공용
// [이유] FlowLine dcc2c3cc FE 과업1 — 디자인 시안(docs/플리오랩/gangwonfolio-trip.jsx EditScreen,
//        gangwonfolio.html AI 보정 바) 의 예시 칩("카페 하나 더"/"1시간 줄이기"/"예산 -5만원"/"맛집 추가")
//        + 입력창 + 전송 버튼 구조를 MUI 토큰 기반으로 재구현(하드코딩 hex 대신 tokens.color 참조).
// [사이드 이팩트] 없음 — 순수 입력 UI, 제출 로직은 onSubmit 콜백에 위임(호출부가 useFolioRefine.propose 연결).
// [수정 시 고려사항] instruction 은 BE refineRequestSchema(1~500자) 와 동일 상한을 TextField maxLength 로 강제.
//                   칩 클릭은 입력값을 대체(누적 아님) — 시안(EditScreen setRefine(s))과 동일 동작.

import { useState, type KeyboardEvent } from 'react';
import { Box, Chip, IconButton, InputBase, Stack } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

const c = tokens.color;
const L = 'light' as const;

const EXAMPLE_CHIPS = ['카페 하나 더', '1시간 줄이기', '예산 -5만원', '맛집 추가'] as const;
const MAX_INSTRUCTION_LENGTH = 500;

export interface RefineInputBarProps {
  onSubmit: (instruction: string) => void;
  /** propose 진행 중 — 입력/전송 비활성 + 스피너 표시 */
  isSubmitting: boolean;
  /** 소유자가 아니거나 Day 가 없는 등 기능 자체를 숨겨야 할 때 부모가 렌더 자체를 생략(이 prop 은 미세 비활성용) */
  disabled?: boolean;
}

/**
 * [목적] AI 보정 입력 바 본체
 * [이유] 시안과 동일한 "예시 칩 → 입력 대체 → 전송" 플로우를 접근성 요구사항(aria-label, Enter 제출,
 *        포커스 인디케이터)과 함께 구현.
 * [사이드 이팩트] 제출 성공 여부와 무관하게 이 컴포넌트는 자체 입력값을 비우지 않음 — 실패 시 재전송이
 *                쉽도록 호출부가 성공 시에만 별도로 입력을 지우고 싶다면 key prop 리마운트로 처리할 것
 *                (현재 화면들은 다이얼로그가 열리며 입력 바 자체는 그대로 두는 UX 를 채택).
 * [수정 시 고려사항] EXAMPLE_CHIPS 문구는 디자인 시안 고정값 — 문구 변경 시 시안 파일과 별개로 이 배열만 수정.
 */
export function RefineInputBar({
  onSubmit,
  isSubmitting,
  disabled = false,
}: RefineInputBarProps): JSX.Element {
  const [value, setValue] = useState('');

  const trimmed = value.trim();
  const canSubmit = !disabled && !isSubmitting && trimmed.length > 0;

  function handleSubmit(): void {
    if (!canSubmit) return;
    onSubmit(trimmed);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') handleSubmit();
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${tokens.spacing[2]}px` }}>
      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: '2px' }}>
        {EXAMPLE_CHIPS.map((label) => (
          <Chip
            key={label}
            label={label}
            size="small"
            clickable
            disabled={disabled || isSubmitting}
            onClick={() => setValue(label)}
            sx={{
              flexShrink: 0,
              bgcolor: c.paper[1][L],
              border: `1px solid ${c.line.base[L]}`,
              color: c.ink.mid[L],
              fontSize: 11,
              fontWeight: 500,
              '&:focus-visible': { outline: `2px solid ${c.brand[500][L]}`, outlineOffset: '2px' },
            }}
          />
        ))}
      </Stack>
      <Box
        sx={{
          display: 'flex',
          gap: `${tokens.spacing[2]}px`,
          alignItems: 'center',
          bgcolor: c.paper[1][L],
          border: `1px solid ${c.line.base[L]}`,
          borderRadius: `${tokens.radius.m}px`,
          p: '5px 6px 5px 12px',
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ fontSize: 14, color: c.brand[500][L] }}>
          ✦
        </Box>
        <InputBase
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, MAX_INSTRUCTION_LENGTH))}
          onKeyDown={handleKeyDown}
          disabled={disabled || isSubmitting}
          placeholder='"카페 더 추가해줘" / "예산 줄여줘"'
          inputProps={{ 'aria-label': 'AI 보정 입력', maxLength: MAX_INSTRUCTION_LENGTH }}
          sx={{ flex: 1, fontSize: 12.5, color: c.ink.high[L] }}
        />
        <IconButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-label="AI 보정 전송"
          sx={{
            bgcolor: c.brand[500][L],
            color: '#fff',
            width: 32,
            height: 32,
            '&.Mui-disabled': { bgcolor: c.line.base[L], color: c.ink.low[L] },
            '&:focus-visible': { outline: `2px solid ${c.brand[500][L]}`, outlineOffset: '2px' },
          }}
        >
          {isSubmitting ? '…' : '↗'}
        </IconButton>
      </Box>
    </Box>
  );
}
